import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button, ModalFooter, Checkbox } from '@folio/stripes/components';
import { FormattedMessage } from 'react-intl';
import { useItemsDeletionValidation } from '../hooks/useItemsDeletionValidation';

function ConfirmDeleteHoldingModal({
  open,
  onClose,
  onRemove,
  holdingRecord,
  itemsCount = 0,
  items = [], // Added items array for detailed validation
}) {
  // Validate items for deletion
  const itemsAnalysis = useItemsDeletionValidation(items);

  // Determine if deletion is possible
  const canDeleteHolding = itemsCount === 0; // No items
  const canDeleteWithItems = itemsAnalysis.canDeleteAll; // All items are available
  const canProceed = canDeleteHolding || canDeleteWithItems;

  // Memoized footer to prevent infinite re-renders
  const renderFooter = useMemo(() => (
    <ModalFooter>
      <Button
        onClick={() => onRemove(canDeleteWithItems)}
        buttonStyle="danger"
        marginBottom0
        disabled={!canProceed}
      >
        <FormattedMessage
          id="ui-plugin-inventory-templates.holdingManagement.deleteModal.confirm"
          defaultMessage={canDeleteWithItems ? 'Delete Holding & Items' : 'Delete Holding'}
        />
      </Button>
      <Button
        onClick={onClose}
        buttonStyle="secondary"
        marginBottom0
      >
        <FormattedMessage
          id="ui-plugin-inventory-templates.holdingManagement.deleteModal.cancel"
          defaultMessage="Cancel"
        />
      </Button>
    </ModalFooter>
  ), [onRemove, canDeleteWithItems, canProceed, onClose]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      footer={renderFooter}
      label={
        <FormattedMessage
          id="ui-plugin-inventory-templates.holdingManagement.deleteModal.title"
          defaultMessage="Delete Holding"
        />
      }
      size="small"
    >
      <div style={{ padding: '20px' }}>
        {/* Main deletion message */}
        <p>
          <FormattedMessage
            id="ui-plugin-inventory-templates.holdingManagement.deleteModal.message"
            values={{
              holdingNumber: holdingRecord?.holdingCopyNumber || '?',
              itemsCount,
            }}
            defaultMessage="Are you sure you want to delete Holding {holdingNumber}? This will also delete {itemsCount} item(s) associated with this holding."
          />
        </p>

        {/* Items analysis section - only if there are items */}
        {itemsAnalysis.hasItems && (
          <div style={{ marginTop: '16px' }}>
            {/* Items analysis info */}
            <div style={{
              backgroundColor: '#f0f9ff',
              border: '1px solid #0ea5e9',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '12px'
            }}
            >
              <h4 style={{ margin: '0 0 8px 0', color: '#0c4a6e' }}>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.holdingManagement.deleteModal.itemsAnalysis"
                  defaultMessage="Items Analysis:"
                />
              </h4>
              <div style={{ fontSize: '14px', color: '#0c4a6e' }}>
                <FormattedMessage
                  id="ui-plugin-inventory-templates.holdingManagement.deleteModal.availableItems"
                  values={{ count: itemsAnalysis.availableCount }}
                  defaultMessage="• Available items: {count}"
                />
                <br />
                <FormattedMessage
                  id="ui-plugin-inventory-templates.holdingManagement.deleteModal.unavailableItems"
                  values={{ count: itemsAnalysis.unavailableCount }}
                  defaultMessage="• Unavailable items: {count}"
                />
              </div>
            </div>

            {/* Automatic deletion notice for available items */}
            {itemsAnalysis.canDeleteAll ? (
              <div style={{
                backgroundColor: '#fff3cd',
                border: '1px solid #fbbf24',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '12px'
              }}
              >
                <p style={{ margin: 0, fontWeight: 'bold', color: '#92400e' }}>
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.holdingManagement.deleteModal.autoDeleteNotice"
                    defaultMessage="ℹ️ All items will be automatically deleted with this holding"
                  />
                </p>
                <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#92400e' }}>
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.holdingManagement.deleteModal.allItemsAvailable"
                    defaultMessage="All items in this holding are available and will be deleted automatically."
                  />
                </p>
              </div>
            ) : (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '12px'
              }}
              >
                <p style={{ margin: 0, fontWeight: 'bold', color: '#dc2626' }}>
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.holdingManagement.deleteModal.cannotDeleteItems"
                    defaultMessage="⚠️ Cannot delete holding: {unavailableCount} items are not available"
                    values={{ unavailableCount: itemsAnalysis.unavailableCount }}
                  />
                </p>
                <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#7f1d1d' }}>
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.holdingManagement.deleteModal.itemsRequirement"
                    defaultMessage="All items must have 'Available' status before deletion can proceed."
                  />
                </p>
              </div>
            )}

            {/* List of unavailable items */}
            {itemsAnalysis.hasUnavailableItems && (
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '12px'
              }}
              >
                <h5 style={{ margin: '0 0 8px 0', color: '#dc2626' }}>
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.holdingManagement.deleteModal.unavailableItemsList"
                    defaultMessage="Unavailable Items:"
                  />
                </h5>
                <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                  {itemsAnalysis.unavailableItems.map((item, index) => (
                    <div
                      key={item.id || index}
                      style={{
                        padding: '4px 0',
                        borderBottom: '1px solid #fecaca',
                        fontSize: '12px',
                        color: '#7f1d1d'
                      }}
                    >
                      <FormattedMessage
                        id="ui-plugin-inventory-templates.holdingManagement.deleteModal.unavailableItem"
                        values={{
                          barcode: item.barcode || 'No barcode',
                          status: item.status?.name || 'Unknown'
                        }}
                        defaultMessage="• {barcode} - Status: {status}"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Original warning for items > 0 (preserved for backward compatibility) */}
        {itemsCount > 0 && !itemsAnalysis.hasItems && (
          <div
            style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: '4px',
              padding: '12px',
              marginTop: '16px',
            }}
          >
            <p style={{ margin: 0, fontWeight: 'bold', color: '#856404' }}>
              <FormattedMessage
                id="ui-plugin-inventory-templates.holdingManagement.deleteModal.warning"
                defaultMessage="Warning: This action cannot be undone and will permanently delete all items in this holding."
              />
            </p>
          </div>
        )}
      </div>
    </Modal>
  );
}

ConfirmDeleteHoldingModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onRemove: PropTypes.func,
  holdingRecord: PropTypes.object,
  itemsCount: PropTypes.number,
  items: PropTypes.arrayOf(PropTypes.object), // Added items prop for detailed validation
};

export default ConfirmDeleteHoldingModal;

